export type TBreakpoints = {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
};

export type TTheme = {
  colors: {
    white: string;
    black: string;
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
    active: string;
    activeHover: string;
    successPrimary: string;
    successPrimaryHover: string;
    successSecondary: string;
    error: string;
    errorHover: string;
    errorBackground: string;
    process: string;
    line: string;
    disabledButtonBG: string;
    textPrimary: string;
    textDeaccent: string;
    textDisabled: string;
    textDisabledHover: string;
    textMuted: string;
    textSuccess: string;
    brandOrange: string;
    brandRed: string;
    brandPink: string;
    brandPurple: string;
    surfaceBase: string;
    surfaceFront: string;
    surfaceBottom: string;
    disabled: string;
    link: string;
  };
  accountsDropdown: {
    backgroundColor: string;
    borderColor: string;
    listItemHoverColor: string;
  };
  pagination: {
    borderColor: string;
    backgroundColor: string;
  };
  bomTable: {
    backgroundColor: string;
  };
  loader: {
    backgroundColor: string;
  };
  notification: {
    successBackground: string;
    warningBackground: string;
  };
  toast: {
    successBackground: string;
    warningBackground: string;
  };
  sideBar: {
    line: string;
  };
  filters: {
    datepickerStartRange: string;
    activeLight: string;
    checkboxMark: string;
    scrollbarTrack: string;
    inputFieldHint: string;
    lightGrayHover: string;
    lightBlue3: string;
    lightBlue2: string;
    mediumBlue: string;
    mediumBlue2: string;
    mediumBlue3: string;
    mediumBlueHover: string;
    mediumBlueHover1: string;
    mediumBlueActive: string;
    darkBlue: string;
    darkBlue1: string;
    darkBlue2: string;
    darkBlueHover: string;
    lightGray: string;
    lightGray1: string;
    lightGray3: string;
    lightGray4: string;
    lightGray5: string;
    lightGray6: string;
    grey: string;
  };
  richEditor: {
    border: string;
  };
  shadows: {
    card: string;
    button: string;
    table: string;
    header: string;
    box: string;
    rangeSlider: string;
    tableInfoPopover: string;
    paper: string;
    tab: string;
    tooltip: string;
  };
  breakpoints: TBreakpoints;
  up: (breakpoint: number, vertical?: boolean) => string;
  down: (breakpoint: number, vertical?: boolean) => string;
  between: (breakpointMin: number, breakpointMax: number, vertical?: boolean) => string;
  gutter: string;
  alertModal: {
    successBackground: string;
    errorBackground: string;
    statusChangeBackground: string;
    processBackground: string;
  };
  select: {
    hoverBackground: string;
    hoverColor: string;
  };
};

export const theme: TTheme = {
  colors: {
    primary: '#191545',
    primaryHover: '#22226D',
    secondary: '#5375BF',
    secondaryHover: '#4e6bb1',
    active: '#E1275F',
    activeHover: '#d2275e',
    successPrimary: '#5CC689',
    successPrimaryHover: '#5cb77a',
    successSecondary: '#689EEE',
    error: '#EE7D52',
    errorHover: '#df7051',
    errorBackground: '#fdf2ee',
    process: '#F8BF45',
    line: '#7691CC',
    disabledButtonBG: '#EEEEEE',
    textPrimary: '#434867',
    textDeaccent: '#666F94',
    textDisabled: '#697080',
    textDisabledHover: '#606676',
    textMuted: '#6c757d',
    textSuccess: '#28a745',
    surfaceBase: '#f5f9ff',
    surfaceFront: '#ebeff7',
    surfaceBottom: '#f2f5fa',
    white: '#FFFFFF',
    black: '#000',
    brandOrange: '#f48120',
    brandRed: '#ec2b50',
    brandPink: '#a84599',
    brandPurple: '#542c81',
    disabled: '#A7ABC3',
    link: '#466ABA'
  },
  accountsDropdown: {
    backgroundColor: '#100f38',
    borderColor: '#8fa2c9',
    listItemHoverColor: '#f5f6ff'
  },
  notification: {
    successBackground: '#d9f2e3',
    warningBackground: '#fdf0eb'
  },
  toast: {
    successBackground: '#eff9f3',
    warningBackground: '#fef9ec'
  },
  pagination: {
    borderColor: '#F1F4F8',
    backgroundColor: '#F1F4F8'
  },
  bomTable: {
    backgroundColor: '#a9badf'
  },
  sideBar: {
    line: '#b5bce6'
  },
  loader: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)'
  },
  filters: {
    datepickerStartRange: '#fce9ef',
    activeLight: '#ffc5d6',
    checkboxMark: '#bfc7d7',
    scrollbarTrack: '#F3F3FB',
    inputFieldHint: '#D1D3D4',
    lightBlue3: '#A7ABC3',
    lightBlue2: '#8fa2c9',
    mediumBlue: '#282A3C',
    mediumBlue2: '#2a2374',
    mediumBlue3: '#495057',
    mediumBlueHover: '#3c5da3',
    mediumBlueHover1: '#4062ac',
    mediumBlueActive: '#395799',
    darkBlue: '#191545',
    darkBlue1: '#100f38',
    darkBlue2: '#070726',
    darkBlueHover: '#22226D',
    lightGray: '#EBEFF7',
    lightGray1: '#999999',
    lightGray3: '#aaaaaa',
    lightGray4: '#cccccc',
    lightGray5: '#e2e5ec',
    lightGray6: '#f1f1f1',
    lightGrayHover: '#dce0e8',
    grey: '#495057'
  },
  richEditor: {
    border: '#466ABA'
  },
  shadows: {
    card: '0px 0px 8px #7691CC',
    button: '0px 3px 5px 0px rgba(25, 21, 69, 0.2);',
    table: '0px 0px 5px #191545',
    header: '0 4px 10px 0 rgba(0, 0, 0, 0.08);',
    box: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
    rangeSlider: '0 3px 6px -3px rgba(0,0,0,.7)',
    tableInfoPopover: '0px 5px 15px rgba(0, 0, 0, 0.25)',
    paper: '0 2px 5px 0 rgba(25,21,69,.2)',
    tab: '0 -3px 5px 0 rgba(83, 117, 191, 0.1)',
    tooltip: '0 0 20px rgba(0, 0, 0, 0.15)'
  },
  breakpoints: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 1024,
    xl: 1399
  },
  up: (breakpoint: number, vertical = false) => `@media (min-${vertical ? 'height' : 'width'}: ${breakpoint + 0.02}px)`,
  down: (breakpoint: number, vertical = false) => `@media (max-${vertical ? 'height' : 'width'}: ${breakpoint}px)`,
  between: (breakpointMin: number, breakpointMax: number, vertical = false) =>
    `@media (max-${vertical ? 'height' : 'width'}: ${breakpointMax}px) and (min-${vertical ? 'height' : 'width'}: ${
      breakpointMin + 0.02
    }px)`,
  gutter: '15px',
  alertModal: {
    successBackground: '#F0FAF4',
    errorBackground: '#FDF1EC',
    statusChangeBackground: '#E6F0FF',
    processBackground: 'yellow' // TODO
  },
  select: {
    hoverBackground: '#f7f8fa',
    hoverColor: '#a7abc3'
  }
};
